import Vue from "vue";
import { Route } from "vue-router";

import { api } from "@tt-app/api";
import { ROUTES } from "@tt-app/constants";
import {
  ApiExerciseAction,
  ApiExerciseBtn,
  ChatState,
  ExerciseFinalScore,
  ExerciseState,
  ExerciseType,
  IsExerciseHandler,
  PayloadExerciseInfo,
  PayloadExerciseEnd,
  TrophyScore,
  TrophyState,
  DrillState,
  ApiDrillReview,
  DrillFinalScore,
  RespExerciseEnd,
  PayloadDrillEnd,
  PayloadDrillStart,
  ApiExercise,
  ApiExerciseBase,
  ApiExerciseBaseContent,
  DrillAllObjects
} from "@tt-app/models/exercise";
import { getAuthInfo } from "@tt-app/utils/internal/vuex";
import { MainLogger } from "@tt-app/utils/internal/logger";

import { BaseHandler } from "./base";

class ExerciseHandler extends BaseHandler implements IsExerciseHandler {
  // --------------------------------------------------------------------------
  //  Initialisation
  // --------------------------------------------------------------------------
  constructor() {
    super();
  }

  // --------------------------------------------------------------------------
  //  Methods: ExerciseHandler
  // --------------------------------------------------------------------------
  buildTrophyState<V extends Vue>(
    component: V,
    exerciseState: ExerciseState
  ): TrophyState | undefined {
    if (!exerciseState.results) {
      // TODO: error
      return undefined;
    }

    const { finalScore, trophyInfo } = exerciseState.results;

    // Build trophy comment
    const trophyComment =
      trophyInfo.trophy === 3
        ? component.$__("ex.trophy.gold")
        : trophyInfo.trophy === 2
        ? component.$__("ex.trophy.silver", trophyInfo.moreCorrectAnswers)
        : component.$__("ex.trophy.bronze", trophyInfo.moreCorrectAnswers);

    return {
      score: trophyInfo.trophy as TrophyScore,
      newRecord: trophyInfo.trophy > trophyInfo.prevBestTrophy,
      imgUrl: undefined,
      comment: trophyComment,
      skippedCount: 0,
      ...finalScore,
      ...trophyInfo
    };
  }

  getNewState(type: ExerciseType): ExerciseState {
    this._debug(`Exercise.getNewState`);

    return {
      assignmentId: undefined,
      exerciseType: type,
      header: { courseTitle: "", exerciseTitle: "" },
      footer: { disabled: false, show: false, buttons: [] },
      logId: -1,
      results: undefined,
      status: "initial",
      trophy: undefined
    };
  }

  async getAuthExerciseInfo<V extends Vue>(
    component: V,
    state: ExerciseState
  ): Promise<PayloadExerciseInfo> {
    const authInfo = await getAuthInfo(component.$store.getters);

    const courseUuid = component.$route.params.courseUuid;
    const exerciseUuid = component.$route.params.exerciseUuid;

    if (!courseUuid) {
      this._warn(`CourseUuid is not available!`);
    }
    if (!exerciseUuid) {
      this._warn(`ExerciseUuid is not available!`);
    }

    const problemCountQuery = (component.$route.query.problemCount as string) || "10";
    const problemCount = parseInt(problemCountQuery, 10);

    const schoolGradeQuery = (component.$route.query.schoolGrade as string) || "5";
    const schoolGrade = parseInt(schoolGradeQuery, 10);

    return {
      ...authInfo,

      courseUuid: component.$route.params.courseUuid,
      exerciseUuid: component.$route.params.exerciseUuid,
      exerciseType: state.exerciseType,
      schoolGrade,
      problemCount
    };
  }

  checkAssignmentId(state: ExerciseState, from: Route): ExerciseState {
    if (from.name === ROUTES.ASSIGNMENT.DETAILS.NAME) {
      let assignmentId: number | undefined;
      try {
        assignmentId = from.params ? parseInt(from.params["assignmentId"], 10) : undefined;
        state.assignmentId = assignmentId;
      } catch (err) {
        MainLogger.warn(`checkAssignmentId: invalid assignmentID: ${assignmentId}`);
      }
    }

    return state;
  }

  loadFooterFromChat(component: any, state: ExerciseState, chatState: ChatState): ExerciseState {
    const buttons: ApiExerciseBtn[] = (chatState.display.lastItem as ApiExerciseAction).buttons
      ? (chatState.display.lastItem as ApiExerciseAction).buttons || []
      : [];

    let disabled: boolean, show: boolean;

    switch (chatState.status) {
      case "MsgBotPlaying":
      case "ProblemDisplaying":
      case "ProblemProcessing":
      case "ProblemFinished":
        show = true;
        disabled = true;
        break;
      case "ProblemWaitingForUserAnswer":
      case "IntroWaitForUser":
      case "ShowTrophy":
      case "Ended":
        show = true;
        disabled = false;
        break;
      case "Initializing":
      case "IntroBotPlaying":
      default:
        disabled = true;
        show = false;
    }

    const nextButton = buttons.find(button => {
      return button.action === "NEXT_EXERCISE";
    });

    if (nextButton) {
      nextButton.text = component.nextExerciseButtonText;
    }

    return {
      ...state,
      footer: { disabled, show, buttons }
    };
  }

  loadFooterFromDrill<V extends Vue>(
    component: V,
    state: ExerciseState,
    drillState: DrillState
  ): ExerciseState {
    let buttons: ApiExerciseBtn[] = [];

    const pages = drillState.practiceMode
      ? (drillState.data?.exercise.practicePages as ApiExerciseBaseContent<DrillAllObjects>[])
      : (drillState.data?.exercise.pages as ApiExerciseBaseContent<DrillAllObjects>[]);

    const currentIndex = drillState.practiceMode
      ? drillState.currentIndex + 1
      : drillState.currentIndex + 1;

    const limitedModeActive = Boolean((component as any).session?.limitedMode?.id);

    let firstButton: ApiExerciseBtn[];

    switch (drillState.display) {
      case "drill-question":
        if (drillState.currentIndex === drillState.exercises.length - 1) {
          buttons = [{ text: component.$__("drill.nav.skip-last"), action: "SKIP" }];
        } else {
          buttons = [{ text: component.$__("drill.nav.skip"), action: "SKIP" }];
        }
        break;
      case "drill-answered":
        if (drillState.currentIndex === drillState.exercises.length - 1) {
          buttons = [
            { text: component.$__("drill.nav.continue-last"), action: "NEXT", color: "primary" }
          ];
        } else {
          buttons = [
            { text: component.$__("drill.nav.continue"), action: "NEXT", color: "primary" }
          ];
        }
        break;
      case "trophy":
        buttons = drillState.data
          ? pages[currentIndex].body
              .filter(comp => comp.type === "DRILL_TROPHY")
              .map(comp => (comp as ApiDrillReview).buttons)
              .find(() => true) || []
          : [];
        break;
      case "drill-review":
        if (state.results?.finalScore.incorrectCount || state.results?.finalScore.skippedCount) {
          firstButton = [
            { text: component.$__("exercise.retry-as-practice"), action: "TRY_AGAIN" }
          ];
        } else {
          firstButton = [{ text: component.$__("exercise.retry-as-test"), action: "TRY_AGAIN" }];
        }

        if (limitedModeActive) {
          firstButton = [{ text: component.$__("exercise.try-again"), action: "TRY_AGAIN" }];
        }

        buttons = [
          ...firstButton,
          { text: component.$__("drill.nav.continue"), action: "NEXT_EXERCISE", color: "primary" }
        ];
        break;
      default:
      // do nothing
    }

    const nextButton = buttons.find(button => {
      return button.action === "NEXT_EXERCISE";
    });

    if (nextButton) {
      nextButton.text = (component as any).nextExerciseButtonText;
    }

    return {
      ...state,
      footer: {
        disabled: false,
        show: true,
        buttons
      }
    };
  }

  loadFooterFromPractice<V extends Vue>(
    component: V,
    state: ExerciseState,
    drillState: DrillState
  ): ExerciseState {
    let buttons: ApiExerciseBtn[] = [];

    switch (drillState.display) {
      case "drill-question":
        if (drillState.currentIndex === drillState.exercises.length - 1) {
          buttons = [{ text: component.$__("practice.nav.skip-last"), action: "SKIP" }];
        } else {
          buttons = [{ text: component.$__("drill.nav.skip"), action: "SKIP" }];
        }
        break;
      case "drill-answered":
        if (drillState.currentIndex === drillState.exercises.length - 1) {
          buttons = [
            { text: component.$__("exercise.end"), action: "FINISH" },
            { text: component.$__("drill.nav.continue-last"), action: "NEXT", color: "primary" }
          ];
        } else {
          buttons = [
            { text: component.$__("drill.nav.continue"), action: "NEXT", color: "primary" }
          ];
        }
        break;
      case "drill-review":
      case "trophy":
        buttons = drillState.data
          ? drillState.data.exercise.pages[drillState.currentIndex].body
              .filter(comp => comp.type === "DRILL_REVIEW" || comp.type === "DRILL_TROPHY")
              .map(comp => (comp as ApiDrillReview).buttons)
              .find(() => true) || []
          : [];
        break;
      default:
      // do nothing
    }

    return {
      ...state,
      footer: {
        disabled: false,
        show: true,
        buttons
      }
    };
  }

  loadHeader(state: ExerciseState, data?: ApiExercise<ApiExerciseBase<any>>): ExerciseState {
    return {
      ...state,
      header: data
        ? {
            chapterTitle: data?.chapter?.title,
            courseTitle: data.course?.title,
            exerciseTitle: data.exercise?.title,
            sectionTitle: data?.section?.title
          }
        : state.header
    };
  }

  loadHeaderFromChat(state: ExerciseState, chatState: ChatState): ExerciseState {
    return this.loadHeader(state, chatState.data);
  }

  loadHeaderFromDrill(state: ExerciseState, drillState: DrillState): ExerciseState {
    return this.loadHeader(state, drillState.data);
  }

  async startExercise<V extends Vue>(component: V, state: ExerciseState): Promise<ExerciseState> {
    const infoPayload = await this.getAuthExerciseInfo(component, state);
    const session = (component as any).session;

    const startResp = await api.exercise.startExercise({
      ...infoPayload,
      assignmentId: state.assignmentId,
      limitedModeId: session?.limitedMode?.id,
      sessionId: session?.id
    });

    this._debug(`Exercise.startExercise, request:\n${JSON.stringify(infoPayload)}`);
    this._debug(`Exercise.startExercise, received:\n${JSON.stringify(startResp)}`);
    this._info(`Exercise.startExercise with logId ${startResp.logId}`);

    return {
      ...state,
      status: "in-progress",
      logId: startResp.logId,
      footer: {
        ...state.footer,
        // Manually clear the buttons list to avoid rapid-clicking the button
        buttons: []
      }
    };
  }

  async startExerciseDrill<V extends Vue>(
    component: V,
    state: ExerciseState,
    drill: DrillState
  ): Promise<ExerciseState> {
    if (!drill.data) {
      MainLogger.warn(`Cannot start drill exercise without loaded data`);
      return state;
    }
    const session = (component as any).session;

    const infoPayload = await this.getAuthExerciseInfo(component, state);
    const drillStart: PayloadDrillStart = {
      ...infoPayload,
      assignmentId: state.assignmentId,
      cardSetUuid: drill.data.exercise.cardSetUuid,
      limitedModeId: session?.limitedMode?.id,
      sessionId: session?.id
    };

    const startResp = await api.exercise.startExercise(drillStart);

    this._debug(`Exercise.startExercise, request:\n${JSON.stringify(infoPayload)}`);
    this._debug(`Exercise.startExercise, received:\n${JSON.stringify(startResp)}`);
    this._info(`Exercise.startExercise with logId ${startResp.logId}`);

    return {
      ...state,
      status: "in-progress",
      logId: startResp.logId
    };
  }

  async startPractice<V extends Vue>(
    component: V,
    state: ExerciseState,
    drill: DrillState
  ): Promise<ExerciseState> {
    if (!drill.data) {
      MainLogger.warn(`Cannot start drill exercise without loaded data`);
      return state;
    }

    const infoPayload = await this.getAuthExerciseInfo(component, state);
    const drillStart: PayloadDrillStart = {
      ...infoPayload,
      assignmentId: state.assignmentId,
      cardSetUuid: drill.data.exercise.cardSetUuid
    };

    const startResp = await api.exercise.startPractice(drillStart);

    this._debug(`Exercise.startExercise, request:\n${JSON.stringify(infoPayload)}`);
    this._debug(`Exercise.startExercise, received:\n${JSON.stringify(startResp)}`);
    this._info(`Exercise.startExercise with logId ${startResp.logId}`);

    return {
      ...state,
      status: "in-progress",
      logId: startResp.logId
    };
  }

  async endExercise<V extends Vue>(
    component: V,
    state: ExerciseState,
    finalScore: ExerciseFinalScore
  ): Promise<ExerciseState> {
    const infoPayload = await this.getAuthExerciseInfo(component, state);
    let endResp: RespExerciseEnd | undefined = undefined;

    if ((finalScore as DrillFinalScore).skippedCount !== undefined) {
      // Drill end exercise
      // Other exercises
      const payload: PayloadDrillEnd = {
        ...infoPayload,
        logId: state.logId,
        numCorrectAnswers: finalScore.correctCount,
        numIncorrectAnswers: finalScore.incorrectCount,
        numSkipped: (finalScore as DrillFinalScore).skippedCount
      };

      endResp = await api.exercise.endExerciseDrill(payload);
    } else {
      // Other exercises
      const payload: PayloadExerciseEnd = {
        ...infoPayload,
        logId: state.logId,
        numCorrectAnswers: finalScore.correctCount,
        numIncorrectAnswers: finalScore.incorrectCount
      };

      endResp = await api.exercise.endExercise(payload);
    }

    if (!endResp) {
      return state;
    }

    this._info(`Exercise.endExercise with trophy ${endResp.trophy}`);

    const newExerciseState: ExerciseState = {
      ...state,
      status: "finished",
      results: {
        finalScore,
        trophyInfo: endResp
      }
    };

    const newTrophyState = this.buildTrophyState(component, newExerciseState);
    newExerciseState.trophy = newTrophyState;

    return newExerciseState;
  }

  async endPractice<V extends Vue>(
    component: V,
    state: ExerciseState,
    finalScore: ExerciseFinalScore
  ): Promise<ExerciseState> {
    const infoPayload = await this.getAuthExerciseInfo(component, state);
    let endResp: RespExerciseEnd | undefined = undefined;

    if ((finalScore as DrillFinalScore).skippedCount !== undefined) {
      // Drill end exercise
      // Other exercises
      const payload: PayloadDrillEnd = {
        ...infoPayload,
        logId: state.logId,
        numCorrectAnswers: finalScore.correctCount,
        numIncorrectAnswers: finalScore.incorrectCount,
        numSkipped: (finalScore as DrillFinalScore).skippedCount
      };

      endResp = await api.exercise.endPracticeDrill(payload);
    } else {
      // Other exercises
      const payload: PayloadExerciseEnd = {
        ...infoPayload,
        logId: state.logId,
        numCorrectAnswers: finalScore.correctCount,
        numIncorrectAnswers: finalScore.incorrectCount
      };

      endResp = await api.exercise.endExercise(payload);
    }

    if (!endResp) {
      return state;
    }

    this._info(`Exercise.endExercise with trophy ${endResp.trophy}`);

    const newExerciseState: ExerciseState = {
      ...state,
      status: "finished",
      results: {
        finalScore,
        trophyInfo: endResp
      }
    };

    const newTrophyState = this.buildTrophyState(component, newExerciseState);
    newExerciseState.trophy = newTrophyState;

    return newExerciseState;
  }
}

export const exerciseHandler = new ExerciseHandler();
